import { useEffect, useRef, useState, type FormEvent } from "react";
import { PasswordField } from "../components/PasswordField";
import { IconFingerprint } from "../components/icons";
import { type Developer, devApi, setCsrf, setSessionToken } from "../developer/api";
import { loginWithBiometricToken, registerBiometricCredential } from "../lib/biometric-session";
import { isNativeApp } from "../lib/mobile-ui";
import {
  authenticateBiometricLogin,
  biometricUserMessage,
  getBiometricStatus,
  publicBiometricApiError,
  publicPasswordLoginError,
  rememberNativeLogin,
  waitForNativeBridge,
  type BiometricMethod,
  type BiometricStatus,
} from "../lib/native-bridge";

export function LoginPage({ onLoggedIn, native = false }: { onLoggedIn: (d: Developer) => void; native?: boolean }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bioBusy, setBioBusy] = useState<BiometricMethod | null>(null);
  const [bio, setBio] = useState<BiometricStatus | null>(null);
  const [bridged, setBridged] = useState(false);
  const filledEmail = useRef(false);

  useEffect(() => {
    if (!native && !isNativeApp()) return;
    let cancelled = false;
    void (async () => {
      const ready = await waitForNativeBridge();
      if (!ready || cancelled) return;
      setBridged(true);
      const status = await getBiometricStatus();
      if (cancelled || !status) return;
      setBio(status);
      if (status.email && !filledEmail.current) {
        filledEmail.current = true;
        setEmail(status.email);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [native]);

  const persistDeviceToken = async (nextEmail: string) => {
    await rememberNativeLogin(nextEmail);
    const status = await getBiometricStatus();
    if (!status) return;
    if (!status.fingerprintEnabled && !status.faceEnabled && !status.genericEnabled) return;
    const token = await registerBiometricCredential();
    if (token) await rememberNativeLogin(nextEmail, token);
  };

  const signIn = async (nextEmail: string, nextPassword: string) => {
    const body = await devApi<{ developer: Developer; csrfToken: string; sessionToken?: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email: nextEmail.trim(), password: nextPassword }),
    });
    if (body.sessionToken) setSessionToken(body.sessionToken);
    setCsrf(body.csrfToken);
    if (native || isNativeApp()) {
      await persistDeviceToken(nextEmail.trim());
    }
    onLoggedIn(body.developer);
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(publicPasswordLoginError(err));
    } finally {
      setBusy(false);
    }
  };

  const biometricLogin = async (method: BiometricMethod) => {
    setBioBusy(method);
    setError(null);
    try {
      const result = await authenticateBiometricLogin(method);
      if (!result.ok || !result.token) {
        setError(biometricUserMessage(result));
        return;
      }
      if (result.email) setEmail(result.email);
      const body = await loginWithBiometricToken(result.token);
      if (body.sessionToken) setSessionToken(body.sessionToken);
      setCsrf(body.csrfToken);
      onLoggedIn(body.developer);
    } catch (err) {
      setError(publicBiometricApiError(err));
    } finally {
      setBioBusy(null);
    }
  };

  const locked = busy || bioBusy != null;
  const loginMethod: BiometricMethod | null = bio?.fingerprintLoginEnabled
    ? "fingerprint"
    : bio?.faceLoginEnabled
      ? "face"
      : bio?.genericLoginEnabled
        ? "generic"
        : null;
  const showFingerprint = Boolean(bridged && loginMethod);

  const form = (
    <form className={`device-form${native ? " mobile-login-card" : " devportal-card"}`} data-dashcam-login={native ? "1" : undefined} onSubmit={(ev) => void submit(ev)}>
      <h2>Sign in</h2>
      <label>
        Email
        <input
          type="text"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={locked}
        />
      </label>
      <label>
        Password
        <PasswordField name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={locked} />
      </label>
      {error && <p className="form-error">{error}</p>}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={locked}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </div>
    </form>
  );

  if (native) {
    return (
      <div className="mobile-login">
        <div className="mobile-login-brand">
          <div className="mobile-login-logo">
            <img src="/logo.png" alt="Dashcam" />
          </div>
          <h1>Dashcam</h1>
          <p>Record. Protect. Secure.</p>
        </div>
        {form}
        {showFingerprint && loginMethod ? (
          <div className="mobile-login-biometrics">
            <div className="mobile-login-fingerprint">
              <button
                type="button"
                className="mobile-login-fingerprint-btn"
                aria-label="Sign in with fingerprint"
                disabled={locked}
                onClick={() => void biometricLogin(loginMethod)}
              >
                <IconFingerprint />
              </button>
              <span>{bioBusy ? "Waiting for fingerprint…" : "Fingerprint Login"}</span>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="devportal login-screen">
      <header className="topbar">
        <div>
          <h1>Master Login</h1>
          <p>One account for cameras, developer tools, API keys, and SDKs.</p>
        </div>
      </header>
      <main className="devportal-main">{form}</main>
    </div>
  );
}
